import { motion, useReducedMotion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { FadeUp, InviewMaskLine } from "./Reveal";

// Local brand SVGs (white, single-colour, from the Simple Icons set) +
// typographic wordmarks for brands without a freely usable icon.
const ICONS = [
  "ford", "audi", "bmw", "porsche", "bentley", "rollsroyce", "ferrari",
  "lamborghini", "maserati", "toyota", "volkswagen", "jeep", "mclaren",
  "astonmartin", "bugatti", "koenigsegg",
];
const WORDMARKS = ["MERCEDES-BENZ", "LAND ROVER", "RANGE ROVER", "LEXUS"];

// Cinematic floating-logo field. Each logo appears ONCE in the DOM and loops
// from off-screen right to off-screen left with its own speed, depth layer and
// slow rotateY wobble — no duplicated content, no jumps, no horizontal overflow.
export default function BrandShowcase() {
  const { t } = useLang();
  const n = t("nx.showcase");
  const reduced = useReducedMotion();
  const items = [...ICONS.map((s) => ({ type: "icon", key: s })), ...WORDMARKS.map((w) => ({ type: "word", key: w }))];

  if (reduced) {
    return (
      <section className="hairline-t bg-[#070809] px-5 md:px-10 py-24" data-testid="brand-showcase">
        <div className="eyebrow mb-10">{n.eyebrow}</div>
        <div className="flex flex-wrap gap-x-12 gap-y-8 items-center opacity-80">
          {ICONS.map((s) => (
            <img key={s} src={`/logos/${s}.svg`} alt={s} width="72" height="26" loading="lazy" className="h-6 w-auto opacity-70" />
          ))}
          {WORDMARKS.map((w) => (
            <span key={w} className="mono text-[12px] tracking-[0.3em] text-[#8A8A86]">{w}</span>
          ))}
        </div>
        <p className="mono text-[10px] text-[#555654] mt-10 uppercase tracking-[0.14em]">{n.disclaimer}</p>
      </section>
    );
  }

  return (
    <section className="hairline-t bg-[#070809] px-5 md:px-10 py-24 md:py-32 overflow-hidden relative" data-testid="brand-showcase">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
        <div>
          <FadeUp><div className="eyebrow mb-8">{n.eyebrow}</div></FadeUp>
          <h2 className="font-display text-[30px] sm:text-[44px] text-[#F3F3F0]">
            <InviewMaskLine>{n.title}</InviewMaskLine>
          </h2>
        </div>
        <FadeUp delay={0.15} className="max-w-sm">
          <p className="text-[13px] leading-relaxed text-[#8A8A86]">{n.sub}</p>
        </FadeUp>
      </div>

      <div className="relative h-[220px] md:h-[280px] -mx-5 md:-mx-10" style={{ perspective: "1200px" }} data-testid="brand-field">
        {/* spotlight / vignette lighting */}
        <div className="absolute inset-0 pointer-events-none z-10" style={{ background: "radial-gradient(ellipse 60% 90% at 50% 45%, rgba(209,199,189,0.10), transparent 60%), linear-gradient(180deg, #070809 0%, transparent 18%, transparent 82%, #070809 100%)" }} />
        {items.map((it, i) => {
          const far = i % 3 === 0; // depth layering: far logos are dimmer, slower, smaller
          const duration = far ? 46 - (i % 5) * 2 : 30 - (i % 6) * 2;
          const delay = -(i * 7.3 + (far ? 12 : 0));
          const top = 18 + ((i * 37) % 55); // deterministic vertical spread (%)
          return (
            <div
              key={it.key}
              className="absolute"
              style={{ top: `${top}%`, animation: `veyrone-drift ${duration}s linear ${delay}s infinite` }}
              data-testid={`brand-item-${it.key}`}
            >
              <motion.div
                animate={{ rotateY: [8, -8, 8], rotateX: [2, -2, 2] }}
                transition={{ repeat: Infinity, duration: 7 + (i % 4), ease: "easeInOut", delay: -(i * 0.9) }}
                style={{ transformStyle: "preserve-3d" }}
                className={far ? "opacity-40" : "opacity-80"}
              >
                {it.type === "icon" ? (
                  <img
                    src={`/logos/${it.key}.svg`}
                    alt={it.key}
                    width="120" height="44" loading="lazy"
                    className={`h-7 md:h-9 w-auto ${far ? "" : ""}`}
                    style={{ filter: "drop-shadow(0 0 18px rgba(231,229,223,0.18))" }}
                  />
                ) : (
                  <span
                    className={`mono tracking-[0.3em] text-[#E7E5DF] whitespace-nowrap ${far ? "text-[12px] md:text-[14px]" : "text-[14px] md:text-[18px]"}`}
                    style={{ textShadow: "0 0 24px rgba(231,229,223,0.25)" }}
                  >
                    {it.key}
                  </span>
                )}
              </motion.div>
            </div>
          );
        })}
      </div>
      <p className="mono text-[10px] text-[#555654] mt-12 uppercase tracking-[0.14em]" data-testid="brand-disclaimer">{n.disclaimer}</p>
    </section>
  );
}
